// Companion/blocks/useStudySetFlashcardRating.ts
//
// Rating a flashcard from inside the thread (ORA-677).
//
// The full study surface rates through `useFlashcardResponse`, which hands the
// rating to the run's pending-response queue on the store; a card served in
// chat has no such run to queue against — the deck's run is local — so the
// rating is written straight through here, the moment the student gives it.
// The FSRS step itself is shared (`studySetFlashcardRun.nextSetCardScheduling`),
// so a card rated here comes back at the same time as the same card rated in
// study, and the deck holds the result because it also has to move the card
// through its own local queues.
//
// A write that fails is logged and dropped: the deck has no queue to hold it
// in, and a retry UI for a mis-fired rating in a three-card deck is not worth
// building (design 4.4). It is the same silence the run's queue leaves a
// student in — there, too, nothing is said and the rating waits for a flush
// that may never come.

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { postFlashcardResponse } from '@/services/flashcardRun';
import { incrementTodayFlashcardTotal } from '@/slices/todayStudyHistory/todayStudyHistorySlice';
import type { FlashcardScheduling } from '@/types/Flashcard';
import type { ChatStudySetFlashcard } from '@/types/chatBlocks';

export interface StudySetRatingOptions {
  /** The FSRS result the deck already computed to move the card's queue. */
  scheduling: FlashcardScheduling;
  /** True when the rating took the card out of the deck's queues. */
  graduated: boolean;
}

export interface UseStudySetFlashcardRating {
  /** Rate the card and write it. */
  rate: (
    card: ChatStudySetFlashcard,
    response: number,
    options: StudySetRatingOptions
  ) => void;
  /** The student's rating mode, so the deck draws two buttons or four. */
  responseOptions: string | undefined;
}

export const useStudySetFlashcardRating = (
  setId: string
): UseStudySetFlashcardRating => {
  const dispatch = useDispatch();

  const responseOptions = useSelector(
    (state: RootState) => state.user.profile?.responseOptions
  );

  const rate = useCallback(
    (
      card: ChatStudySetFlashcard,
      response: number,
      options: StudySetRatingOptions
    ) => {
      void postFlashcardResponse({
        flashcardId: card.id,
        response,
        scheduling: options.scheduling,
        flashcardVariantId: card.variantId || undefined,
        // The server marks the set item done on any respond that carries the
        // set id, so it only rides the rating that graduated the card.
        studySetId: options.graduated ? setId : undefined,
      }).catch((error) => {
        console.error('Failed to persist flashcard response:', error);
      });

      dispatch(incrementTodayFlashcardTotal());
    },
    [dispatch, setId]
  );

  return { rate, responseOptions };
};
